import React, { createContext, useContext, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import clsx from "clsx";
import Typography from "@material-ui/core/Typography";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import PropTypes from "prop-types";
import MySnackbar from "./MySnackbar.jsx";

const useAlertStyles = makeStyles(() => ({
  dialogRoot: {
    "& .MuiDialog-paper": {
      borderRadius: 16,
    },
    "& .MuiDialogTitle-root": {
      textAlign: "center",
    },
  },
  actionsRoot: {
    display: "block",
    "& .MuiButton-containedPrimary .MuiButton-label": {
      fontSize: 16,
      fontWeight: 500,
    },
    "& .MuiButton-root": {
      fontSize: 16,
      fontWeight: 500,
      textTransform: "none",
      height: 44,
      borderRadius: 22,
      marginTop: 8,
    },
    "&.MuiDialogActions-spacing > :not(:first-child)": {
      marginLeft: 0,
    },
    "& .MuiButton-containedSecondary": {
      backgroundColor: "#226C36",
    },
  },
  dialogContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  titledDialogContent: {
    marginBottom: 15,
    paddingBottom: 16,
  },
  // отображать переводы строк в сообщении алерта
  dialogMessage: {
    whiteSpace: "pre-line",
  },
}));

/**
 * @param {Boolean} open
 * @param {Function} handleClose
 * @param {String} title
 * @param {String} message
 * @param {React.Component} content
 * @param {String} confirmText
 * @param {String} cancelText
 * @param {Function} onConfirm
 * @param {Function} onCancel
 * @param {Boolean} disableEscape
 * @param {String} maxWidth
 * @returns {*}
 * @constructor
 */
export const Alert = ({
  open,
  handleClose,
  title,
  message,
  content,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  disableEscape,
  maxWidth,
}) => {
  const classes = useAlertStyles();

  return (
    <Dialog
      className={classes.dialogRoot}
      maxWidth={maxWidth}
      disableBackdropClick={disableEscape}
      disableEscapeKeyDown={disableEscape}
      open={open}
      onClose={() => {
        handleClose();
      }}
    >
      {title && <DialogTitle>{title}</DialogTitle>}
      <DialogContent className={clsx({ [classes.titledDialogContent]: !!title }, classes.dialogContent)}>
        {message && <Typography className={classes.dialogMessage}>{message}</Typography>}
        {content}
      </DialogContent>
      <DialogActions className={classes.actionsRoot}>
        {cancelText && (
          <Button
            onClick={() => {
              onCancel && onCancel();
              handleClose();
            }}
            color="primary"
            fullWidth
            variant="contained"
          >
            {cancelText}
          </Button>
        )}
        {confirmText && (
          <Button
            onClick={() => {
              onConfirm && onConfirm();
              handleClose();
            }}
            color="secondary"
            fullWidth
            autoFocus
            variant="contained"
          >
            {confirmText}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

Alert.propTypes = {
  title: PropTypes.string,
  message: PropTypes.string,
  content: PropTypes.node,
  handleClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
  disableEscape: PropTypes.bool,
  maxWidth: PropTypes.string,
};

Alert.defaultProps = {
  disableEscape: true,
  maxWidth: "xs",
};

/**
 * @typedef {Object} SnackbarProviderContext
 * @property {FnSnackbar} error
 * @property {FnSnackbar} warn
 * @property {FnSnackbar} info
 * @property {FnSnackbar} success
 * @property {FnAlert} alert
 */

/**
 * @callback FnAlert
 * @param {AlertOptions} options
 */

/**
 * @callback FnSnackbar
 * @param {String} msg
 * @param {{vertical: String, horizontal: String}} [pos]
 * @returns {void}
 */

/**
 * @typedef {Object} AlertOptions
 * @property {String} [title]
 * @property {String} [message]
 * @property {React.ReactNode|HTMLElement} [content]
 * @property {String} [confirmText]
 * @property {String} [cancelText]
 * @property {Function} [onConfirm]
 * @property {Function} [onCancel]
 * @property {Boolean} [disableEscape]
 * @property {string} [maxWidth]
 */

/** @type {React.Context<SnackbarProviderContext>} */
const dialogsContext = createContext({
  /** @type {FnSnackbar} */
  error: () => {},
  /** @type {FnSnackbar} */
  warn: () => {},
  /** @type {FnSnackbar} */
  info: () => {},
  /** @type {FnSnackbar} */
  success: () => {},
  /** @type {FnAlert} */
  alert: () => {},
});

/**
 * @returns {SnackbarProviderContext}
 */
export const useDialogs = () => useContext(dialogsContext);

/**
 * @param {React.ReactNode} children
 * @returns {*}
 * @constructor
 */
const DialogsProvider = ({ children }) => {
  const [isSnackbarOpened, setSnackbarOpened] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarPosition, setSnackbarPosition] = useState();
  const [alertConfirmText, setAlertConfirmText] = useState(/** @type {String|undefined} */ undefined);
  const [alertCancelText, setAlertCancelText] = useState(/** @type {String|undefined} */ undefined);
  const [alertOnConfirm, setAlertOnConfirm] = useState(null);
  const [alertOnCancel, setAlertOnCancel] = useState(null);
  const [isAlertOpened, setAlertOpened] = useState(false);
  const [alertTitle, setAlertTitle] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [alertContent, setAlertContent] = useState(null);
  const [alertDisableEscape, setAlertDisableEscape] = useState(/** @type {Boolean|undefined} */ undefined);
  const [alertMaxWidth, setAlertMaxWidth] = useState("xs");
  const alertCloseTimeoutHandleRef = useRef(0);

  /** */
  const openSnackbar = (pos, type, msg) => {
    setSnackbarPosition(pos);
    setSnackbarSeverity(type || "info");
    setSnackbarMessage(msg);
    setSnackbarOpened(true);
  };

  /** @type {FnSnackbar} */
  const error = (msg, pos) => {
    openSnackbar(pos, "error", msg);
  };

  /** @type {FnSnackbar} */
  const warn = (msg, pos) => {
    openSnackbar(pos, "warning", msg);
  };

  /** @type {FnSnackbar} */
  const info = (msg, pos) => {
    openSnackbar(pos, "info", msg);
  };

  /** @type {FnSnackbar} */
  const success = (msg, pos) => {
    openSnackbar(pos, "success", msg);
  };

  /** */
  const closeSnackbar = () => {
    setSnackbarOpened(false);
    setTimeout(() => {
      setSnackbarSeverity("info");
      setSnackbarMessage("");
      setSnackbarPosition(undefined);
    }, 300);
  };

  /**
   * @type {FnAlert}
   */
  const alert = (options) => {
    const {
      title = "",
      message = "",
      content = null,
      disableEscape = false,
      maxWidth = "xs",
      confirmText,
      cancelText,
      onConfirm,
      onCancel,
    } = options;
    clearTimeout(alertCloseTimeoutHandleRef.current);
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertContent(content);
    setAlertConfirmText(confirmText);
    setAlertCancelText(cancelText);
    setAlertOnConfirm(() => onConfirm);
    setAlertOnCancel(() => onCancel);
    setAlertDisableEscape(disableEscape);
    setAlertMaxWidth(maxWidth);
    setAlertOpened(true);
  };

  /** */
  const alertCloseHandler_ = () => {
    alertCloseTimeoutHandleRef.current = setTimeout(() => {
      setAlertDisableEscape(undefined);
      setAlertContent(null);
      setAlertMessage("");
      setAlertConfirmText(undefined);
      setAlertCancelText(undefined);
      setAlertOnConfirm(null);
      setAlertOnCancel(null);
      setAlertTitle("");
      setAlertMaxWidth("xs");
    }, 300);
    setAlertOpened(false);
  };

  /** @type SnackbarProviderContext */
  const value = {
    error,
    warn,
    info,
    success,
    alert,
  };

  return (
    <dialogsContext.Provider value={value}>
      {children}
      <MySnackbar
        isOpen={isSnackbarOpened}
        close={closeSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
        position={snackbarPosition}
      />
      <Alert
        title={alertTitle}
        message={alertMessage}
        content={alertContent}
        confirmText={alertConfirmText}
        cancelText={alertCancelText}
        onConfirm={alertOnConfirm}
        onCancel={alertOnCancel}
        disableEscape={alertDisableEscape}
        open={isAlertOpened}
        handleClose={alertCloseHandler_}
        maxWidth={alertMaxWidth}
      />
    </dialogsContext.Provider>
  );
};

export default DialogsProvider;
